/* eslint-disable react-hooks/exhaustive-deps */
import { MoveLeft } from "lucide-react";
import React from "react";
import { getI18n, useTranslation } from "react-i18next";
import { Icons } from "../../components/icons";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader } from "../../components/ui/card";
import { Label } from "../../components/ui/label";
import { Separator } from "../../components/ui/separator";
import fetchData from "../../utils/api";
import { Config } from "../../utils/config";
import { dateFormat } from "../../utils/date-format";
import OrderDetails from "./OrderDetails";
import TableReservationDetails from "./TableReservationDetails";
import ErrorBoundary from "../../ErrorBoundary";
import Cashback from "./Cashback";
import { set } from "date-fns";

export default function NewPlaceOrder() {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = React.useState(false);
  const [orderDetails, setOrderDetails] = React.useState<any>({});
  const [isOrderDetail, setIsOrderDetails] = React.useState(false);
  const [taxLabel, setTaxLabel] = React.useState<any>();
  const [cartId, setCartId] = React.useState<any>();
  const [authToken, setAuthToken] = React.useState<any>("");
  const [restaurantId, setRestaurantId] = React.useState<any>();
  const [restaurantDetail, setRestaurantDetail] = React.useState<any>({});
  const [paymentStatus, setPaymentStatus] = React.useState<string | null>(null);
  const [paymentIntent, setPaymentIntent] = React.useState<string | null>(null);
  const [currentLanguage, setCurrentLanguage] = React.useState<string | null>(
    null
  );
  React.useEffect(() => {
    const url = new URL(window.location.href);
    const params = url.searchParams;
    setPaymentStatus(params.get("redirect_status"));
    setPaymentIntent(params.get("payment_intent"));
    setCartId(params.get("cartId"));
    setAuthToken(params.get("authToken"));
    setRestaurantId(params.get("id"));
    setCurrentLanguage(params.get("language"));
  }, []);

  const fetchRestaurantDetail = async () => {
    setIsLoading(true);
    try {
      const restaurantDetail = await fetchData({
        url: "restaurant",
        method: "POST",
        token: authToken,
      });

      if (restaurantDetail.status_code === 200) {
        setRestaurantDetail(restaurantDetail.data);
      }
    } catch (error) {
      console.log("error", error);
      setTimeout(fetchRestaurantDetail, 1000);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchOrderDetails = async () => {
    const body: any = { cart_id: cartId };
    const res = await fetchData({
      url: "orders/get-placed-order",
      method: "POST",
      body,
      token: authToken,
    });
    return res;
  };

  const handleFetchOrderDetails = async () => {
    try {
      const res = await fetchOrderDetails();
      if (res.status_code === 200) {
        const orderingMethod =
          res.data?.orderingMethod === "dine_in"
            ? "in_restaurant"
            : res.data?.orderingMethod;
        setOrderDetails(res.data);

        const order: any = Config.env.app.ordering_method.find(
          (item) => item.value === res.data.orderingMethod
        )?.label;
        const transactionData = {
          eventType: "purchase",
          orderingMethod: t(order),
          transactionAmount:
            res.data.orderingMethod === "table_reservation"
              ? ""
              : res.data.totalAmount,
          transactionId: res.data.id,
          email: res.data.email,
          phone: res.data.contact,
        };

        window.parent.postMessage(transactionData, "*");
        if (res.data?.orderingMethod !== "table_reservation") {
          setTaxLabel(
            res.data?.taxDetails ??
              Array?.from(
                res.data?.orderDetails
                  ?.reduce((map, item) => {
                    if (item.tax_category[orderingMethod] !== undefined) {
                      const taxName = item.tax_category.tax_category;
                      const taxRate = item.tax_category[orderingMethod];
                      const totalTax =
                        (item.countTotal * Number(taxRate)) / 100;

                      if (map.has(taxName)) {
                        // If the tax name already exists, add the totalTax to the existing value
                        map.get(taxName).totalTax += totalTax;
                      } else {
                        // If the tax name does not exist, create a new entry
                        map.set(taxName, {
                          name: taxName,
                          tax: taxRate,
                          totalTax: totalTax,
                        });
                      }
                    }
                    return map;
                  }, new Map())
                  .values()
              )
          );
        }
        const subTotal = res.data?.orderDetails?.reduce((acc, item) => {
          return acc + Number(item.countTotal);
        }, 0);
        localStorage.removeItem("cart");
        setIsLoading(false);
      } else {
        setTimeout(handleFetchOrderDetails, 1000); // Retry after 1 second
      }
    } catch (error) {
      console.log("error", error);
      setTimeout(handleFetchOrderDetails, 1000); // Retry after 1 second in case of error
    }
  };

  const handleBackToMenu = (success?: boolean) => {
    const url = new URL(window.location.href);
    const params = url.searchParams;
    if (orderDetails?.orderingMethod === "table_reservation") {
      localStorage.removeItem("cart");

      if (params.has("type")) {
        params.delete("cartId");
        params.delete("authToken");
        params.delete("redirect_status");
        // params.set("return_token", localStorage.getItem("return_token") ?? "");
        params.set("type", "table_reservation");
        const newUrl = `${url.origin}?${params.toString()}`;
        window.location.href = newUrl;
      }
    } else {
      if (success === false) {
      } else {
        localStorage.removeItem("cart");
      }
      params.delete("cartId");
      params.delete("authToken");
      params.delete("redirect_status");
      // params.set("return_token", localStorage.getItem("return_token") ?? "");
      const newUrl = `${url.origin}?${params.toString()}`;
      window.location.href = newUrl;
    }
  };

  const fetchOrderStatus = async () => {
    const body = { cart_id: cartId };
    return await fetchData({
      url: "orders/get-order-status",
      method: "POST",
      body,
      token: authToken,
    });
  };

  const handleOrderDetails = () => {
    setIsOrderDetails(true);
  };

  React.useEffect(() => {
    if (cartId && authToken && paymentStatus === "succeeded") {
      const placeOrder = async () => {
        let body = {
          cart_id: cartId,
          language: currentLanguage,
          paymentIntent: paymentIntent,
        };
        const res = await fetchData({
          url: "cart/place-online-order",
          method: "POST",
          body,
          token: authToken,
        });

        if (res.status === "success") {
          fetchRestaurantDetail();
          handleFetchOrderDetails();

          const statusInterval = setInterval(async () => {
            try {
              const statusResponse = await fetchOrderStatus();
              if (statusResponse.status === "success") {
                if (
                  statusResponse.data !== orderDetails.status?.toLowerCase()
                ) {
                  setOrderDetails((prevDetails) => ({
                    ...prevDetails,
                    status: statusResponse.data,
                  }));
                }
              }
            } catch (error) {
              console.error("Error fetching order status:", error);
            }
          }, 5000);

          return () => clearInterval(statusInterval);
        }
      };
      placeOrder();
    }
    if (cartId && authToken) {
      fetchRestaurantDetail();
      handleFetchOrderDetails();

      const statusInterval = setInterval(async () => {
        try {
          const statusResponse = await fetchOrderStatus();
          if (statusResponse.status === "success") {
            if (statusResponse.data !== orderDetails.status?.toLowerCase()) {
              setOrderDetails((prevDetails) => ({
                ...prevDetails,
                status: statusResponse.data,
              }));
            }
          }
        } catch (error) {
          console.error("Error fetching order status:", error);
        }
      }, 5000);

      return () => clearInterval(statusInterval);
    }
  }, [cartId, authToken, restaurantId, paymentStatus]);

  React.useEffect(() => {
    if (currentLanguage) {
      const i18n = getI18n();
      i18n.changeLanguage(currentLanguage);
    }
  }, [currentLanguage]);

  // Payment Failed UI
  if (
    paymentStatus === "failed" ||
    paymentStatus === "pending" ||
    paymentStatus === "cancelled"
  ) {
    const success = false;
    return isLoading ? (
      <div className="h-[100vh] flex justify-center items-center py-4 flex-col">
        <Icons.spinner className="size-5 animate-spin" />
        <h2>{t("please_wait")}</h2>
        <p className="text-sm mt-2 text-center">
          {t("order_history.did_you_know")}
        </p>
      </div>
    ) : (
      <div className="w-full flex h-[100vh] justify-center items-center p-4 bg-background max-w-5xl mx-auto">
        <div className="flex justify-center items-center h-[100vh] w-full">
          <Card className="w-full max-w-md">
            <CardHeader>
              <div className="space-y-1">
                <div className="flex items-center gap-3">
                  <button
                    className="items-center"
                    onClick={() => handleBackToMenu(success)}
                  >
                    <MoveLeft className="text-red cursor-pointer" />
                  </button>
                  <h3 className="text-red font-semibold text-lg sm:text-xl">
                    {t("payment_failed.title")}
                  </h3>
                </div>
              </div>
            </CardHeader>
            <Separator />
            <CardContent className="p-6 space-y-6">
              <Card className="border-red-500">
                <CardContent className="p-4">
                  <div className="flex items-start gap-2">
                    <Icons.alertCircle className="h-5 w-5 text-red-500 mt-0.5" />
                    <div>
                      <h4 className="text-red-500 font-semibold">
                        {t("payment_failed.title")}
                      </h4>
                      <p className="text-sm">
                        {t("payment_failed.description")}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="flex flex-col space-y-4">
                <p className="text-sm text-muted-foreground">
                  {t("payment_failed.long_description")}
                </p>

                <div className="flex flex-col space-y-2 sm:flex-row sm:space-y-0 sm:space-x-2 sm:justify-center">
                  <Button
                    variant="outline"
                    onClick={() => handleBackToMenu(success)}
                    className="w-full sm:w-auto"
                  >
                    {t("place_order.back_to_menu")}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="w-full flex h-[100vh] justify-center items-center p-4 bg-background max-w-5xl mx-auto">
        <div className="flex justify-center items-center h-[100vh] w-full">
          {isLoading ? (
            <div className="h-[100vh] flex justify-center items-center py-4 flex-col">
              <Icons.spinner className="size-5 animate-spin" />
              <h2>{t("please_wait")}</h2>
              <p className="text-sm mt-2 text-center">
                {t("order_history.did_you_know")}
              </p>
            </div>
          ) : !isOrderDetail ? (
            <div className="flex flex-col w-full space-y-4">
              <Card className="w-full space-y-4">
                <CardHeader>
                  <div className="space-y-1">
                    <div className="flex items-center gap-3">
                      <button
                        className="items-center"
                        onClick={() => {
                          handleBackToMenu();
                        }}
                      >
                        <MoveLeft
                          className="text-red cursor-pointer"
                          onClick={() => {
                            handleBackToMenu();
                          }}
                        />
                      </button>
                      <h3 className="text-red font-semibold text-lg sm:text-xl">
                        {orderDetails?.orderingMethod === "table_reservation"
                          ? t("place_order.table_reservation_title")
                          : t("place_order.title")}
                      </h3>
                    </div>

                    <p className="text-sm text-muted-foreground">
                      {`${t("place_order.description_1").replace(
                        "{orderingMethod}",
                        t(
                          Config.env.app.ordering_method.find(
                            (item) => item.value === orderDetails.orderingMethod
                          )?.label as string
                        ).toLowerCase()
                      )}`}
                    </p>
                  </div>
                </CardHeader>
                <Separator />
                <CardContent className="p-4">
                  <div className="md:flex md:justify-between">
                    <div>
                      <div>
                        <Label className="font-bold">
                          {t("place_order.order_placed")}
                        </Label>
                      </div>
                      <div>
                        {orderDetails.createdAt &&
                        restaurantDetail?.timezone ? (
                          <Label>
                            {dateFormat({
                              date: orderDetails.createdAt,
                              timezone: restaurantDetail?.timezone,
                            })}
                          </Label>
                        ) : (
                          <Label>{t("loading")}</Label>
                        )}
                      </div>
                      <div>
                        <Label>
                          {t("order_history.order_status")}
                          {" : "}
                          {t(
                            Config.env.app.order_status.find(
                              (status) => status.value === orderDetails.status
                            )?.label as string
                          )}
                        </Label>
                      </div>

                      {orderDetails?.orderingMethod !== "table_reservation" && (
                        <div>
                          <Label>
                            {t("order_history.payment_method")}
                            {" : "}
                            {t(
                              Config.env.app.payment_method.find(
                                (status) =>
                                  status.value === orderDetails.paymentMethod
                              )?.label as string
                            )}
                          </Label>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center max-md:justify-center">
                      <Button
                        variant={"link"}
                        onClick={() => handleOrderDetails()}
                        className="underline !h-auto text-red text-sm font-medium"
                      >
                        {t("place_order.order_details")}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Cashback />
            </div>
          ) : (
            <Card className="w-full">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div className="flex justify-center text-center items-center">
                    <Button
                      variant={"outline"}
                      className="items-center"
                      onClick={() => {
                        handleBackToMenu();
                      }}
                    >
                      <div className="flex gap-2 items-center">
                        <MoveLeft className="cursor-pointer" />
                        {orderDetails?.orderingMethod === "table_reservation"
                          ? t("place_order.back_to_table_reservation")
                          : t("place_order.back_to_menu")}
                      </div>
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <Separator />
              <CardContent className="p-4 space-y-4">
                <div className="flex justify-end text-end">
                  <div>
                    <div>
                      <Label className="font-bold">
                        {t("place_order.order_placed")}
                      </Label>
                    </div>
                    <div>
                      {orderDetails.createdAt && restaurantDetail?.timezone ? (
                        <Label>
                          {dateFormat({
                            date: orderDetails.createdAt,
                            timezone: restaurantDetail?.timezone,
                          })}
                        </Label>
                      ) : (
                        <Label>{t("loading")}</Label>
                      )}
                    </div>
                  </div>
                </div>
                <Separator />
                {orderDetails?.orderingMethod === "table_reservation" ? (
                  <TableReservationDetails
                    cartList={orderDetails}
                    authToken={authToken}
                  />
                ) : (
                  <ErrorBoundary>
                    <OrderDetails
                      taxLabel={taxLabel}
                      cartList={orderDetails}
                      subtotal={orderDetails.subTotal}
                      total={orderDetails.totalAmount}
                      couponCode={orderDetails.couponCode}
                      couponData={orderDetails.couponDetails}
                      couponDiscount={orderDetails.couponDiscount}
                      restaurantDetail={restaurantDetail}
                      deliveryFees={orderDetails.deliveryFees}
                      deliveryFeesTax={orderDetails.deliveryFeesTax}
                      orderingMethod={orderDetails.orderingMethod}
                      serviceFeesLabel={orderDetails.serviceTaxDetails}
                      tipTaxFees={parseFloat(orderDetails?.tipTaxFee)}
                      totalTipAmount={orderDetails?.totalTip}
                      authToken={authToken}
                      roundOff={orderDetails.roundOff}
                    />
                  </ErrorBoundary>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </>
  );
}
